import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, Button, Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MarkAttendance from '../MarkAttendance';
import { useDispatch } from 'react-redux';
import { verifyAttendance } from '../../../../redux/features/attendanceSlice';
import useHandleApiResponse from '../../../../Hooks/useHandleApiResponse';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import GlobalDialogueContent from '../../../../components/GlobalDialogueContent';

const TopSection = ({ selectedDate, handleDateChange, loginAttendenceView, totalData, setRefresh }) => {
  //RECIVEING THE SELECTED DATE TO SHOW ON THE PICKING DATE FIELD
  /* const dispatch = useDispatch();
  const apiHandleHelper = useHandleApiResponse() */ const verifyeLoader = useSelector(
    (state) => state.attendance.verificationLoading,
  );
  const [warning, setWarning] = useState(false);

  /* const handleVerifyButton = () => {
    const data = { attendance_id: totalData?.attendanceId };

    dispatch(verifyAttendance(data)).then((res) =>
      apiHandleHelper(res, {
        onSuccess: () => {
          setRefresh((prev) => !prev);
        },
      }),
    );
  }; */

  // FIND TODAY'S FIRST BIOMETRIC PUNCH-IN AND RUN A LIVE WORKING-HOURS TIMER
  const today = dayjs();
  const todaysAttendance = totalData?.attendance?.find((item) => item.date === today.date());
  const firstPunchIn = todaysAttendance?.biometricData?.firstTimestamp;
  const lastPunchOut = todaysAttendance?.biometricData?.lastTimestamp;

  // only run the timer when the viewed month/year is the current one and a punch-in exists
  const isCurrentMonth = dayjs(selectedDate).isSame(today, 'month');
  const punchInTime = firstPunchIn && isCurrentMonth ? dayjs(firstPunchIn) : null;
  const punchOutTime = lastPunchOut && isCurrentMonth ? dayjs(lastPunchOut) : null;

  const [elapsed, setElapsed] = useState('');

  const formatDuration = (diff) => {
    const hours = String(Math.floor(diff / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
    const seconds = String(diff % 60).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  // VERIFY WINDOW: opens at 00:00 on the last day of the month and closes at 11:00 AM on the 1st of the next month.
  // `now` is kept ticking so the button disables itself when the window closes without needing a page reload.
  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const interval = setInterval(() => setNow(dayjs()), 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  const isLastDayOfMonth = now.date() === now.endOf('month').date();
  const isGracePeriod = now.date() === 1 && now.hour() < 11;

  // THE MONTH THAT IS BEING CLOSED: the current month on its last day, the previous month during the
  // grace hours on the 1st. Verification is only for that month, never for a month still in progress.
  const verifiableMonth = isGracePeriod ? now.subtract(1, 'month') : now;

  const isVerificationWindowOpen =
    (isLastDayOfMonth || isGracePeriod) && dayjs(selectedDate).isSame(verifiableMonth, 'month');

  useEffect(() => {
    if (!punchInTime) {
      setElapsed('');
      return;
    }

    // PUNCHED OUT: freeze at the final worked duration, no live ticking
    if (punchOutTime) {
      setElapsed(formatDuration(punchOutTime.diff(punchInTime, 'second')));
      return;
    }

    // STILL WORKING: tick every second up to now
    const tick = () => setElapsed(formatDuration(dayjs().diff(punchInTime, 'second')));

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [firstPunchIn, lastPunchOut, isCurrentMonth]);

  return (
    <>
      <GlobalDialog
        title={'Warning'}
        closeButton
        content={
          <GlobalDialogueContent
            cancel
            dispatchFn={verifyAttendance}
            dispatchParams={{ attendance_id: totalData?.attendanceId }}
            loading={verifyeLoader}
            onSuccess={() => {
              setRefresh((prev) => !prev);
            }}
            content={
              'Please ensure the attendance for this month is accurate before proceeding. Once confirmed, this action cannot be undone.'
            }
            buttonLabel="Confirm"
            buttonLoadingLabel="Confirming..."
            setOpen={setWarning}
          />
        }
        open={warning}
        setOpen={setWarning}
      />
      <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              displayStaticWrapperAs="desktop"
              views={['month', 'year']}
              value={selectedDate}
              onChange={handleDateChange}
            />
          </LocalizationProvider>

          {isCurrentMonth && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
              }}
            >
              {punchInTime ? (
                <>
                  <Box
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    {punchOutTime
                      ? `${punchInTime.format('hh:mm A')} - ${punchOutTime.format('hh:mm A')}`
                      : `Working since ${punchInTime.format('hh:mm A')}`}
                  </Box>
                  <Box
                    sx={{
                      fontSize: { xs: '1rem', md: '1.3rem' },
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: punchOutTime ? 'success.main' : 'text.primary',
                    }}
                  >
                    {elapsed}
                  </Box>
                </>
              ) : (
                <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Not punched in yet</Box>
              )}
            </Box>
          )}
        </Box>

        <Stack direction={'row'} alignItems={'center'}>
          <Button
            variant="contained"
            onClick={() => setWarning(true)}
            disabled={
              !(
                isVerificationWindowOpen ||
                (totalData?.exitDate
                  ? dayjs().isAfter(dayjs(totalData.exitDate, 'YYYY-MM-DD').subtract(2, 'day'))
                  : false)
              ) ||
              (totalData?.exitDate ? dayjs().isAfter(dayjs(totalData.exitDate), 'day') : false) ||
              Boolean(totalData?.verification) ||
              verifyeLoader
            }
            color={Boolean(!totalData?.attendanceId) ? 'error' : 'primary'}
          >
            {verifyeLoader ? 'Verifying...' : 'Verify'}
          </Button>
          <Box sx={{ ml: 2 }}>
            <MarkAttendance loginAttendenceView={loginAttendenceView} />
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default TopSection;
